import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type ClipAndBoardListItem, type ListResponse, type PublicBoard } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { GALLERY_MIXED_DATA, getPublicSearchTotalKey } from '~/constants/react-query-keys';
import { useRegisterClipEvents } from '~/hooks/useRegisterClipEvents';
import { useTableViewItems } from '~/swr-hooks/gallery/tableView/useTableViewItems';
import { tableViewDataToUpdateMediaPlayer } from '~/swr-hooks/gallery/tableView/utils';
import { itemIsPublicAsset, type UseGalleryMixedData } from '~/swr-hooks/gallery/types';
import { usePublicMediaPlayerDataUpdate } from '~/swr-hooks/mediaPlayer/usePublicMediaPlayerData';
import { type SearchTotals } from '~/types/SearchTotals';

export type UseBoardChildTableViewItemsParams = {
  dataCacheKey: Record<string, unknown>;
  enabled: boolean;
  queryFn: (pageParam: string) => Promise<ListResponse<ClipAndBoardListItem>>;
  shouldUpdateMediaPlayer?: boolean;
};

/**
 * Mixed table infinite query + media-player/search-total wiring for a drilled board. Callers own
 * the ShortUrl vs workspace queryFn and cache key extras.
 */
export const useBoardChildTableViewItems = ({
  dataCacheKey,
  enabled,
  queryFn,
  shouldUpdateMediaPlayer = true,
}: UseBoardChildTableViewItemsParams): UseGalleryMixedData<PublicBoard, PublicClip> => {
  const { shortId } = useShortIdContext();
  const { workspaceId } = usePublicWorkspace();
  const { updatePublicMediaPlayerData } = usePublicMediaPlayerDataUpdate(shortId);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [GALLERY_MIXED_DATA, dataCacheKey],
    queryFn: ({ pageParam }) => queryFn(pageParam || ''),
    enabled,
    getNextPageParam: (prevPage) => prevPage.pagination.cursor,
    initialPageParam: '',
  });

  const itemsData = useTableViewItems<PublicBoard, PublicClip>({
    data: data?.pages ?? [],
    isLoading,
    hasMore: !!hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
  });

  useEffect(() => {
    if (shouldUpdateMediaPlayer) {
      updatePublicMediaPlayerData(tableViewDataToUpdateMediaPlayer(itemsData));
    }
  }, [itemsData, updatePublicMediaPlayerData, shouldUpdateMediaPlayer]);

  useEffect(() => {
    queryClient.setQueryData<SearchTotals>(getPublicSearchTotalKey(shortId), {
      total: itemsData.total,
    });
  }, [itemsData, queryClient, shortId]);

  const clips = useMemo(() => itemsData.data.filter(itemIsPublicAsset).map((item) => item.item), [itemsData.data]);

  useRegisterClipEvents({
    clips,
    workspaceId,
  });

  return itemsData;
};
